import { useTranslation } from "react-i18next"
import { useEffect, useState } from "react"
import { observer } from "mobx-react-lite"
import { useStore } from "../../contexts/store"
import { PodClass } from "../../classes/Pod"
import { iReadingQuestion } from "../../shared/src/types/Interaction"

import { getUserMessage, isAfterDeadline } from "../Elements/ReadingQuestion"
import Blockquote from "../Elements/Blockquote"
import Deadline from "../Elements/Deadline"
import Link from "../Elements/Link"
import UxIcon from "../Elements/UxIcon"
import { Box, Divider, FormControl, InputLabel, MenuItem, Paper, Select, SelectChangeEvent, useTheme } from "@mui/material"

import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime.js'
import UserInfoAvatar from "../Elements/UserInfoAvatar"
dayjs.extend(relativeTime)


const PodReadingQuestions = ({pod}: {pod: PodClass}) => {
  const { t } = useTranslation()
  const { podStore, sessionStore, uiStore } = useStore()
  const [filteredSection, setFilteredsection] = useState("")
  const session = sessionStore.session
  const color = uiStore.getInteractionColor("readingQuestion")
  const [readingQuestions, setReadingQuestions] = useState<iReadingQuestion[]>([])
  const questionAnswered = []
  const questionOpen = []
  const questionExpired = []

  useEffect(()=> {
    const allReadingQuestions = pod.getAllReadingQuestions()
    setReadingQuestions(allReadingQuestions)
  }, [pod, pod.getAllReadingQuestions])

  if (!session) return null

  const userId = session.user.userId

  // filter if reading question status is open, answered or expired
  for(const readingQuestion of readingQuestions) {
    const thread = podStore.getThread(readingQuestion.interactionId)
    const hasUserMessage = thread ? getUserMessage(thread, userId) : false

    if(hasUserMessage) {
      questionAnswered.push(readingQuestion)
    }
    else {
      const isAfter = isAfterDeadline(readingQuestion)
      if(readingQuestion.openUntil && isAfter) {
        questionExpired.push(readingQuestion)
      }
      else{
        questionOpen.push(readingQuestion)
      }
    }
  }

  const filteredStatusList = []
  filteredStatusList.push({status: "Open", items: questionOpen})
  filteredStatusList.push({status: "Answered", items: questionAnswered})
  filteredStatusList.push({status: "Expired", items: questionExpired})


  function handleSelectChange(e: SelectChangeEvent) {
    setFilteredsection(e.target.value)
  }

  const ReadingQuestion = ({iconColor, readingQuestion}: {iconColor: string, readingQuestion: iReadingQuestion}) => {
    const filename = readingQuestion.anchor.nodeId ? podStore.getFilename(readingQuestion.anchor.nodeId) : ""
    const pageNumber = readingQuestion.anchor.rects.length ?  readingQuestion.anchor.rects[0].p : ""
    return (
      <Link noDecoration={true} to={`/pod/${pod.podId}/pdf/${readingQuestion.anchor.nodeId}/question/${readingQuestion.interactionId}`}>
        <Paper elevation={3} sx={{display: "grid", p: "20px", m: "20px 15px", "&:hover": {backgroundColor: "whitesmoke"} }}>        <Box sx={{
            color: "textcolor.light",
            display: "flex",
            fontSize: "13px",
            justifyContent: "end",
            wordBreak: "break-word"
          }}>
            {filename}: <span style={{fontWeight: 450, paddingLeft: "3px", wordBreak: "keep-all"}}>{pageNumber}</span>
          </Box>
          <Box sx={{
            display: "grid",
            gridTemplateColumns: "auto max-content",
            padding: "3px 0"
          }}>
            <Box sx={{
              alignItems: "start",
              display: "grid",
              gap: "10px",
              gridTemplateColumns: "max-content auto",
              padding: " 13px 0 8px 0"
            }}>
              <UserInfoAvatar podId={pod.podId} userId={readingQuestion.userId} sx={{
                fontSize:"16px",
                height: "30px",
                width: "30px"
              }}/>
              <Box sx={{display: "grid"}}>
                <Box>
                  <Box sx={{fontWeight: 500, paddingTop: "3px"}}>
                    {readingQuestion.label}
                  </Box>
                  <Deadline readingQuestion={readingQuestion} />
                </Box>
                {readingQuestion.anchor.relText !== "" &&
                  <Box sx={{marginLeft: "-25px"}}>
                    <Blockquote color={color} hasToggleButton={false} text={readingQuestion.anchor.relText} />
                  </Box>
                }
              </Box>
            </Box>
          </Box>
        </Paper>
      </Link>
    )
  }

  const StatusSection = ({statusList}: {statusList: {status: string, items: iReadingQuestion[]}}) => {
    const theme = useTheme()
    let iconColor = "grey"
    if(statusList.status === "Open") iconColor = theme.palette.error.light
    if(statusList.status === "Answered") iconColor = theme.palette.success.light

    if(statusList.items.length === 0 || (filteredSection && statusList.status !== filteredSection)) return null
    return (
      <Box sx={{paddingTop: "30px"}}>
        <Box sx={{
          alignContent: "center",
          backgroundColor: "back.light",
          display: "grid",
          gap: "10px",
          gridTemplateColumns: "max-content auto",
          fontSize: "19px",
          fontWeight: 300,
          padding: "8px"
        }}>
          <UxIcon sx={{color: iconColor}} id="status" />
          {t(statusList.status)}
        </Box>
        {statusList.items.map((readingQuestion, index) =>
          <Box key={readingQuestion.interactionId}>
            {index > 0 &&
              <Divider variant="fullWidth" />
            }
            <ReadingQuestion iconColor={iconColor} readingQuestion={readingQuestion} />
          </Box>
        )}
      </Box>
    )
  }

  const SectionFilter = () => (
    <FormControl fullWidth sx={{margin: "15px 0"}}>
      <InputLabel id="filter-by-pdf-label">{t('Filter by status')}</InputLabel>
      <Select
        labelId="filter-by-pdf-label"
        id="filter-by-pdf-select"
        value={filteredSection}
        label={t('Filter by status')}
        onChange={handleSelectChange}
      >
        <MenuItem key="no-filter" value={""}><em>{t('No filter')}</em></MenuItem>
        {questionOpen.length !== 0 &&
          <MenuItem key="open" value={"Open"}>{t('Open')} ({questionOpen.length})</MenuItem>
        }
        {questionAnswered.length !== 0 &&
          <MenuItem key="answered" value={"Answered"}>{t('Answered')} ({questionAnswered.length})</MenuItem>
        }
        {questionExpired.length !== 0 &&
          <MenuItem key="expired" value={"Expired"}>{t('Expired')} ({questionExpired.length})</MenuItem>
        }
      </Select>
    </FormControl>
  )

  return (
    <Box>
      <Box sx={{
        color: "textcolor.main",
        fontSize: "20px",
        fontWeight: 600,
        wordBreak: "break-word"
      }}>
        {t('{{numberOfReadingQuestions}} Reading questions in this Pod', {numberOfReadingQuestions: readingQuestions.length})}
      </Box>
      <Box sx={{
        margin: "10px"
      }}>
        <SectionFilter />
        {filteredStatusList.map(statusList =>
          <StatusSection key={statusList.status} statusList={statusList} />
        )}
      </Box>
    </Box>
  )
}

export default observer(PodReadingQuestions)
